import React, { useState, useId } from 'react';
import { Phone } from 'lucide-react';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  label,
  error,
  required = false,
  disabled = false,
  placeholder = "06 12 34 56 78",
  className = ''
}: PhoneInputProps) {
  const id = useId();
  const [displayValue, setDisplayValue] = useState(formatPhoneNumber(value));
  const [hasBlurred, setHasBlurred] = useState(false);

  // Formater le numéro de téléphone français
  function formatPhoneNumber(phone: string): string {
    // Supprimer tous les caractères non numériques
    const cleaned = phone.replace(/\D/g, '');

    // Limiter à 10 chiffres
    const limited = cleaned.substring(0, 10);

    // Formater avec des espaces par paquets de 2 chiffres
    const formatted = limited.match(/.{1,2}/g)?.join(' ') || limited;

    return formatted;
  }

  // Obtenir la valeur brute (sans espaces)
  function getRawValue(formattedValue: string): string {
    return formattedValue.replace(/\s/g, '');
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Si on essaie de supprimer, on autorise
    if (inputValue.length < displayValue.length) {
      const formatted = formatPhoneNumber(inputValue);
      setDisplayValue(formatted);
      onChange(getRawValue(formatted));
      return;
    }
    
    // Sinon, on formate et on limite
    const formatted = formatPhoneNumber(inputValue);
    
    // Bloquer à 10 chiffres (14 caractères avec les espaces)
    if (getRawValue(formatted).length <= 10) {
      setDisplayValue(formatted);
      onChange(getRawValue(formatted));
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleBlur = () => {
    setHasBlurred(true);
    onBlur?.();
  };

  const inputClasses = `input-field pl-10 ${error ? 'input-error' : ''} ${className}`;

  const rawValue = getRawValue(displayValue);

  return (
    <div className="field-group">
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="form-label-required">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          type="tel"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`${inputClasses} focus-visible`}
          disabled={disabled}
          autoComplete="tel"
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200">
          <Phone className={`h-4 w-4 ${error ? 'text-error-500' : 'text-neutral-400'}`} />
        </div>
      </div>

      {hasBlurred && rawValue.length > 0 && rawValue.length < 10 && !error && (
        <p className="error-message">Le numéro de téléphone doit contenir 10 chiffres</p>
      )}

      {error && <p id={`${id}-error`} className="error-message">{error}</p>}
    </div>
  );
}